import { Card, Space, Tag, Typography } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";
import { Tags } from ".";

/**
 * ContributorCard Component
 *
 * A card component for displaying contributor information with their activity summary,
 * impact areas, and associated statistics.
 *
 * @example
 * ```tsx
 * <ContributorCard
 *   name="John Doe"
 *   activitySummary="Led major infrastructure improvements"
 *   impactArea="velocity"
 *   topItems={["PR #1234", "Issue #567"]}
 *   stats={[
 *     {
 *       metric: "PRs Merged",
 *       value: 15,
 *       evidence: ["PR #1234", "PR #5678"]
 *     }
 *   ]}
 * />
 * ```
 */
export interface ContributorProps {
  /**
   * The contributor's name
   */
  name?: string;

  /**
   * Summary of the contributor's activities
   */
  activitySummary: string;

  /**
   * Area of impact (e.g., 'velocity', 'quality', 'risk')
   */
  impactArea?: string;

  /**
   * Array of top items (PRs, issues, or theme names)
   */
  topItems?: string[];

  /**
   * Temporal patterns of the contributor's activities
   */
  temporalPatterns?: {
    peak_hours: string[];
    peak_days: string[];
    consistency_score: number;
    anomaly_description: string;
    timeseries_evidence: string[];
  };
  /**
   * Array of statistics about the contributor's activities
   */
  stats?: Array<{
    metric: string;
    value: number;
    evidence: string[];
  }>;

  /**
   * Optional CSS class name for custom styling
   */
  className?: string;

  /**
   * Layout variant for the card: 'default' (vertical), 'profile' (horizontal), 'minimal' (text-only)
   */
  variant?: "default" | "profile" | "minimal";
}

export const Contributor: React.FC<ContributorProps> = ({
  name,
  activitySummary,
  impactArea,
  topItems,
  stats,
  temporalPatterns,
  className,
  variant = "default",
}) => {
  if (variant === "profile") {
    // Horizontal profile card
    return (
      <div
        className={[className, "flex-col-full"].filter(Boolean).join(" ")}
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ flex: 0 }}>
              {name && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: 18,
                  }}
                >
                  {stripMarkdown(name ?? "")[0]}
                </div>
              )}
            </div>
            {activitySummary && (
              <div style={{ flex: 1 }}>
                <div className="vzbl-header-5">{stripMarkdown(name ?? "")}</div>
                <div className="vzbl-text-3">
                  <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                    {activitySummary}
                  </Markdown>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Stats/tags row at the bottom */}
        {(stats && stats.length > 0) ||
          (impactArea && (
            <div
              style={{
                marginTop: "auto",
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {stats &&
                stats.length > 0 &&
                stats.slice(0, 3).map((stat, idx) => (
                  <Tag className="vzbl-pill-sm vzbl-pill-dark" key={idx}>
                    {stat.value} {stat.metric}
                  </Tag>
                ))}

              {impactArea && (
                <Tag className="vzbl-pill-sm vzbl-pill-clear">
                  {stripMarkdown(impactArea)}
                </Tag>
              )}
            </div>
          ))}
      </div>
    );
  }
  if (variant === "minimal") {
    // Minimal, text-only card
    return (
      <div
        className={[className, "vzbl-contributor-minimal", "vzbl-text-3"]
          .filter(Boolean)
          .join(" ")}
      >
        <strong>{stripMarkdown(name ?? "")}</strong>:{" "}
        <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
          {activitySummary}
        </Markdown>
      </div>
    );
  }
  // Default vertical card
  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {name && (
          <Typography.Text className="vzbl-header-5 single-line">
            <CustomTooltip content={stripMarkdown(name ?? "")}>
              {stripMarkdown(name)}
            </CustomTooltip>
          </Typography.Text>
        )}
        {impactArea && <Tag className="vzbl-pill-tag">{impactArea}</Tag>}
      </div>
      {activitySummary && (
        <Typography.Text className="vzbl-text-3">
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {activitySummary}
          </Markdown>
        </Typography.Text>
      )}
      {stats && stats.length > 0 && (
        <Space
          key="work-pattern-stats"
          direction="vertical"
          size={4}
          style={{ width: "100%" }}
        >
          {stats.map((stat, idx) => (
            <div key={`stat-${idx}`}>
              <Space size={8}>
                <Typography.Text className="vzbl-text-2">
                  {stat.metric}:
                </Typography.Text>
                <Typography.Text className="vzbl-text-3">
                  {stat.value}
                </Typography.Text>
              </Space>
            </div>
          ))}
        </Space>
      )}

      {temporalPatterns &&
        (temporalPatterns.peak_days?.length > 0 ||
          temporalPatterns.peak_hours?.length > 0) && (
          <Space
            key="work-pattern-temporal-patterns"
            size={4}
            direction="vertical"
          >
            {temporalPatterns.peak_days?.length > 0 && (
              <Typography.Text className="vzbl-text-3">
                <strong>Peak Days:</strong>{" "}
                {temporalPatterns.peak_days.join(", ")}
              </Typography.Text>
            )}
            {temporalPatterns.peak_hours?.length > 0 && (
              <Typography.Text className="vzbl-text-3">
                <strong>Peak Hours:</strong>{" "}
                {temporalPatterns.peak_hours.join(", ")}
              </Typography.Text>
            )}
            {temporalPatterns.timeseries_evidence?.length > 0 && (
              <Typography.Text className="vzbl-text-3">
                <strong>Evidence:</strong>{" "}
                {temporalPatterns.timeseries_evidence.join(", ")}
              </Typography.Text>
            )}
            {temporalPatterns.anomaly_description && (
              <Typography.Text className="vzbl-text-3">
                <strong>Outlier Activity:</strong>{" "}
                {temporalPatterns.anomaly_description}
              </Typography.Text>
            )}
          </Space>
        )}

      {topItems && topItems.length > 0 && (
        <div style={{ width: "100%", marginTop: 8 }}>
          <Tags tags={topItems} tagClassName="vzbl-pill-xsm vzbl-pill-tag" />
        </div>
      )}
    </Space>
  );
};
